export default {
  title: "Administration",
  pageSubtitle: "Roles, users and the library catalogue.",
  overview: {
    nav: "Overview",
    welcome: "Hello, {{name}}",
    welcomeHint: "This is the cataloguing workspace. Catalogue records, error reports, dictionaries, import and statistics.",
    servicesTitle: "Catalogue sections",
    catalogHint: "Entities, merge, dictionaries and NBR catalogue import",
    analyticsHint: "Record counts from the live NBR catalogue",
    usersHint: "Accounts and access roles",
    associationsHint: "Association kinds and group tree",
    auditHint: "Action log and domain audit",
  },
  subtitles: {
    overview: "Catalogue records, error reports, dictionaries, import and statistics.",
    users: "Accounts and assigned roles.",
    roles: "Access roles and their holders.",
    associations: "Association kinds and the group tree.",
    audit: "Catalogue action log.",
    auditEvents: "Event decisions.",
    auditDeposits: "Deposit decisions.",
    entities: "People, works, editions and other LRM entities.",
    nbrOld: "Live view of the previous catalogue. Records are not stored.",
    entityEdit: "Catalogue entity record.",
    merge: "Merge duplicates into a single record.",
    errors: "Reader reports about record errors.",
    dictionaries: "Entity, relation and role types.",
    import: "Upload MARC / RUSMARC records.",
    creative: "Creative occupations dictionary.",
  },
  overviewStats: {
    entitiesTitle: "Catalogue",
    entitiesHint: "Persons, collective agents, works and editions in the NBR catalogue.",
    entitiesTotal: "Total records",
    errorsTitle: "Error reports",
    errorsHint: "New reader reports about inaccuracies.",
    errorsNew: "New",
  },
  overviewServices: {
    entities: "Create and edit catalogue records",
    merge: "Merge duplicate records",
    errors: "Review error reports",
    dictionaries: "Entity, relation and role types",
    import: "Upload MARC / RUSMARC",
  },
  nav: {
    overview: "Overview",
    users: "Users",
    entities: "Entities",
    nbrOld: "NBR",
    merge: "Merge",
    errors: "Error reports",
    dictionaries: "Dictionaries",
    import: "Import",
    audit: "Audit",
    associations: "Associations",
    roles: "Roles",
    creative: "Creative roles",
    auditEvents: "Event audit",
    auditDeposits: "Deposit audit",
  },
  entities: "Entity catalogue",
  nbrOld: {
    hint: "Enter a query",
    relationsHint: "A row opens the record. Relations on the card run a ready-made search.",
    back: "Back to the list",
    cardInNbr: "Record in NBR",
    links: "Relations",
    download: "Download",
    downloadPage: "Download page",
    downloadFailed: "Could not download the record",
    partial: "Some sections or relations did not load",
    noCipher: "This record has no cipher, so it cannot be opened directly",
    linkedResults: "{{label}}",
    openOriginal: "Original",
    year: "Year",
    brief: "Source line",
    open: "Open record",
    yearFrom: "Year from",
    yearTo: "to",
    yearsHint: "A single year can go in either field. The range is at most 50 years.",
    isbnHint: "ISBN search: hyphens are catalogued inconsistently, so a match is not always found.",
    roles: "Roles in editions",
    roleAuthor: "author",
    rolesEmpty: "The union catalogue has no editions with this heading",
    about: "Editions about the person",
    aboutEmpty: "None",
    editionsEmpty: "The union catalogue has no editions with this title",
    editions_one: "{{count}} edition",
    editions_other: "{{count}} editions",
    graphHint: "Point at a node to see the relation",
    suggestions: "Suggestions",
    resultsFor: "Editions: {{label}}",
    reset: "Reset",
    found_one: "Found {{count}} record",
    found_other: "Found {{count}} records",
  },
  createEntity: "New entity",
  createMenu: "Create",
  editEntity: "Edit entity",
  type: "Type",
  label: "Label",
  attributesJson: "Attributes (JSON)",
  invalidJson: "Invalid JSON",
  nomens: "Nomens",
  identifiers: "Identifiers",
  relations: "Relations",
  addNomen: "Add nomen",
  addIdentifier: "Add identifier",
  addRelation: "Add relation",
  text: "Text",
  kind: "Kind",
  scheme: "Scheme",
  value: "Value",
  predicate: "Predicate",
  subject: "Subject",
  object: "Object",
  role: "Role",
  merge: "Merge duplicates",
  mergeTarget: "Target record",
  mergeSources: "Sources (comma-separated UUIDs)",
  mergeAction: "Merge",
  mergeDone: "Records merged",
  duplicates: "Duplicate candidates",
  similarity: "Similarity",
  errorReports: "Error reports",
  status: "Status",
  resolution: "Resolution",
  dictionaries: "Model dictionaries",
  version: "Version",
  importTitle: "Excel import",
  chooseFile: "xlsx file",
  dryRun: "Dry run (no writes)",
  startImport: "Start",
  jobs: "Jobs",
  reindex: "Reindex search",
  reindexDone: "Reindex queued",
  audit: "Audit log",
  actor: "Actor",
  action: "Action",
  entityId: "Entity ID",
  confirmDelete: "Delete this record permanently?",
  constructor: {
    newPerson: "New person",
    newEdition: "New edition",
    details: "Details",
    preview: "Preview",
    lang: "Language",
    unique: "unique",
    choose: "Select",
    noAttributes: "This type has no extra fields.",
    similarFound: "Similar records already exist. Open one if this is the same object.",
    similarInNbrOld: "Similar records exist in NBR. This hint does not block creation.",
    authorForHints: "Author for suggestions",
    worksInNbrOld: "Works by this author in NBR",
    allInNbrOld: "All in NBR",
    identifierTaken: "This identifier is already used. Open the existing record — a duplicate cannot be created.",
    nameRequired: "Add at least one name.",
    titleRequired: "Enter the edition title.",
    authorRequired: "Add at least one creator.",
    workRequired: "Select an existing work.",
    expressionRequired: "Select an existing expression.",
    graphLabel: "Entity relations",
    removeRelation: "Remove relation",
    createRelated: "Create related",
    relatedName: "Name of the related record",
    confirmCreateRelated: "Add as new",
    wizardLabel: "Person creation steps",
    wizardEditionLabel: "Edition creation steps",
    back: "Back",
    next: "Next",
    kindPreferred: "preferred",
    kindVariant: "variant",
    useExisting: "Use existing",
    createNew: "Create new",
    publisherNone: "Skip",
    publisherName: "Publisher name",
    authorName: "New author name",
    addAuthor: "Add author",
    roleAut: "author",
    steps: {
      names: "Names",
      details: "Details",
      identifiers: "Identifiers",
      relations: "Relations",
      preview: "Review",
    },
    editionSteps: {
      manifestation: "Manifestation",
      work: "Work",
      expression: "Expression",
      authors: "Creators",
      publisher: "Publisher",
      preview: "Review",
    },
    enums: {
      gender: {
        male: "male",
        female: "female",
        unknown: "unknown",
      },
    },
  },
  associations: {
    title: "Associations",
    kinds: "Group kinds",
    groups: "Groups",
    emptyKinds: "No group kinds found.",
    emptyGroups: "No groups found.",
    loadFailed: "Could not load associations.",
    requiresApproval: "Requires approval",
  },
  domainAudit: {
    eventsTitle: "Event audit",
    depositsTitle: "Deposit audit",
    empty: "No entries.",
    loadFailed: "Could not load audit.",
    entity: "Entity",
    from: "From",
    to: "To",
    note: "Note",
    when: "When",
  },
  users: {
    title: "Users",
    account: "Account",
    search: "Search loaded users",
    filterStatus: "Status filter",
    status: "Status",
    rolesTitle: "Workspaces",
    caption: "Local accounts and workspace access",
    roleRequired: "Select at least one workspace",
    updateFailed: "The account could not be updated.",
    loadFailed: "Users could not be loaded.",
    statuses: {
      active: "Active",
      blocked: "Blocked",
    },
    roles: {
      reader: "Reader",
      librarian: "Librarian",
      publisher: "Publisher",
      analyst: "Analyst",
      admin: "Administrator",
    },
    cabinets: {
      reader: "Reader",
      writer: "Writer",
      union: "Union administrator",
      organizer: "Event organizer",
      catalog: "Librarian",
      administrator: "Administrator",
      readerLocked: "Base access cannot be removed",
    },
  },
  creative: {
    title: "Creative self-definition",
    empty: "The catalogue is empty.",
    loadFailed: "Could not load the creative roles catalogue.",
    direction: "Direction",
    subdirection: "Subdirection",
    role: "Role",
  },
}
